import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, ChartLineUp, ShieldCheck } from "@phosphor-icons/react";

interface NavigationProps {
  renderButton: (
    to: string,
    children: ReactNode,
    variant?: "primary" | "secondary" | "ghost",
  ) => JSX.Element;
}

const dockItems = [
  {
    icon: <Sparkle size={29} weight="thin" />,
    label: "Features",
    href: "#features",
  },
  {
    icon: <ChartLineUp size={29} weight="thin" />,
    label: "Impact",
    href: "#impact",
  },
  {
    icon: <ShieldCheck size={29} weight="thin" />,
    label: "Security",
    href: "#security",
  },
];

export function Navigation({ renderButton }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="sticky top-0 z-50 bg-[#0a0118] shadow-lg border-b border-[rgba(128,53,255,0.2)]">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/#hero"
            className="flex items-center gap-2.5 z-50 group relative"
          >
            <motion.img
              src="/acr-logo.svg"
              alt="AI Code Review"
              className="h-7 w-auto relative z-10"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            />
            <div className="flex flex-col relative">
              <motion.div
                className="absolute -inset-3 bg-gradient-to-r from-purple-400/10 via-blue-400/10 to-pink-400/10 rounded-lg blur-xl group-hover:blur-2xl transition-all duration-300"
                animate={{ opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
              <span className="text-xl tracking-tight font-display font-bold mix-blend-plus-lighter relative z-10">
                <span className="text-purple-400/90 hover:text-purple-300 transition-colors">
                  A{" "}
                </span>
                <span className="text-blue-400/90 hover:text-blue-300 transition-colors">
                  C{" "}
                </span>
                <span className="text-pink-400/90 hover:text-pink-300 transition-colors">
                  R
                </span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium -mt-1 text-white/60 group-hover:text-white/80 transition-colors relative z-10 ml-[2px]">
                AI Code Review
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-6">
              {dockItems.map((item) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-1.5 group relative py-2 px-4 rounded-lg transition-all duration-300"
                  whileHover={{ y: -1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <motion.div className="absolute inset-0 bg-white/[0.02] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative">
                    <span className="relative z-10 transform group-hover:text-white transition-colors duration-300 text-white/70">
                      {item.icon}
                    </span>
                  </span>
                  <span className="text-sm font-medium text-white/70 group-hover:text-white transition-colors duration-300 relative z-10">
                    {item.label}
                  </span>
                  <motion.div
                    className="absolute inset-x-0 bottom-0 h-[1px] bg-white/10"
                    initial={{ scaleX: 0, opacity: 0 }}
                    whileHover={{ scaleX: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.a>
              ))}
            </div>
            <div className="flex items-center gap-6">
              {renderButton("/login", "Sign In", "ghost")}
              {renderButton("/login", "Start Free")}
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden z-50 p-2 text-white/90 hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  isMobileMenuOpen
                    ? "M6 18L18 6M6 6l12 12"
                    : "M4 6h16M4 12h16M4 18h16"
                }
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[998]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-16 left-0 right-0 bottom-0 bg-[#0a0118] z-[999]"
            >
              <div className="flex flex-col h-[calc(100vh-4rem)]">
                <div className="flex-1 overflow-y-auto py-6 px-6">
                  {dockItems.map((item) => (
                    <motion.a
                      key={item.label}
                      href={item.href}
                      className="flex items-center gap-3 text-lg font-medium text-white/70 hover:text-white py-3 group"
                      onClick={() => setIsMobileMenuOpen(false)}
                      whileHover={{ x: 4 }}
                    >
                      <span className="relative">
                        <span className="relative z-10 text-xl">
                          {item.icon}
                        </span>
                      </span>
                      <span>{item.label}</span>
                    </motion.a>
                  ))}
                </div>
                <div className="p-6 border-t border-white/[0.08] flex items-center gap-8">
                  {renderButton("/login", "Sign In", "ghost")}
                  {renderButton("/login", "Start Free")}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
